import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useBackRoute } from '../hooks/useBackRoute';
import { toValidObjectIdString, formatApiDate } from '../utils/security';
import {
  fetchSiparisler,
  fetchSiparisDetay,
  fetchSiparisSekmeSayilari,
  siparisKargoTeslimAl,
} from '../services/siparisService';
import SiparisDetayModal from '../components/SiparisDetayModal';
import SiparisOlusturModal from '../components/SiparisOlusturModal';
import SiparisTaslaklariPanel from '../components/SiparisTaslaklariPanel';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import TableListFooter from '../components/TableListFooter';
import { PAGE_DEFAULT_LIMIT } from '../constants/pagination';
import { normalizeSayfalama } from '../utils/pagination';

const TABS = [
  { id: 'onay-bekleyen', label: 'Onay Bekleyen', tab: 'onay-bekleyen' },
  { id: 'kargo-bekleyen', label: 'Kargo Bekleyen', durum: 'KargoBekliyor' },
  { id: 'fatura-bekleyen', label: 'Fatura Bekleyen', tab: 'fatura-bekleyen' },
  { id: 'kargoda', label: 'Kargoda', tab: 'kargoda' },
  { id: 'tamamlanan', label: 'Tamamlanan', durum: 'Tamamlandi' },
];

const getSiparisId = (s) => toValidObjectIdString(s?.id ?? s?._id) || s?.siparisNumarasi || '';

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const sekmeSayisiForTpTab = (tabId, veri) => {
  if (!veri || typeof veri !== 'object') return null;
  if (tabId === 'onay-bekleyen') return veri['onay-bekleyen'];
  if (tabId === 'kargo-bekleyen') return veri.durum_KargoBekliyor;
  if (tabId === 'fatura-bekleyen') return veri['fatura-bekleyen'];
  if (tabId === 'kargoda') return veri.kargoda;
  if (tabId === 'tamamlanan') return veri.durum_Tamamlandi;
  return null;
};

const TechServiceSiparisPage = () => {
  const { confirm } = useConfirmDialog();
  const goBack = useBackRoute('/');
  const [siparisler, setSiparisler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('onay-bekleyen');
  const [selectedSiparis, setSelectedSiparis] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createTaslakTip, setCreateTaslakTip] = useState(null);
  const [taslakRefreshKey, setTaslakRefreshKey] = useState(0);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [sekmeSayilari, setSekmeSayilari] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, totalPages: 1 });

  const tabConfig = useMemo(() => TABS.find((t) => t.id === activeTab) || TABS[0], [activeTab]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const refreshSekmeSayilari = useCallback(async () => {
    try {
      const response = await fetchSiparisSekmeSayilari();
      if (response?.basarili && response?.veri) {
        setSekmeSayilari(response.veri);
      }
    } catch {
      setSekmeSayilari(null);
    }
  }, []);

  const loadSiparisler = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { sayfa: currentPage, limit: PAGE_DEFAULT_LIMIT };
      if (tabConfig.tab) params.tab = tabConfig.tab;
      else if (tabConfig.durum) params.durum = tabConfig.durum;
      const response = await fetchSiparisler(params);
      if (response?.basarili && response?.veri) {
        setSiparisler(response.veri.siparisler || []);
        setPaginationMeta(normalizeSayfalama(response.veri.sayfalama));
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Siparişler yüklenemedi'));
      setSiparisler([]);
    } finally {
      setLoading(false);
    }
  }, [tabConfig, currentPage]);

  useEffect(() => {
    loadSiparisler();
  }, [loadSiparisler]);

  useEffect(() => {
    refreshSekmeSayilari();
  }, [activeTab, refreshSekmeSayilari]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const handleViewDetail = async (siparis) => {
    const id = getSiparisId(siparis);
    if (!id) {
      window.showNotification?.('error', 'Sipariş ID alınamadı');
      return;
    }
    try {
      const response = await fetchSiparisDetay(id);
      if (response?.basarili && response?.veri) {
        setSelectedSiparis(response.veri);
        setDetailModalOpen(true);
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Detay yüklenemedi'));
    }
  };

  const handleKargoTeslimAl = async (siparis) => {
    const id = getSiparisId(siparis);
    if (!id) return;
    const ok = await confirm({
      title: 'Kargo teslim alındı',
      message: `${siparis.siparisNumarasi} siparişi için kargo teslim alındı olarak işaretlensin mi?`,
      variant: 'info',
      confirmLabel: 'Evet, işaretle',
    });
    if (!ok) return;
    setActionLoading(id);
    try {
      const response = await siparisKargoTeslimAl(id);
      if (response?.basarili) {
        window.showNotification?.('success', 'Kargo teslim alındı, sipariş tamamlandı');
        loadSiparisler();
        refreshSekmeSayilari();
        setDetailModalOpen(false);
        setSelectedSiparis(null);
      } else {
        window.showNotification?.('error', response?.mesaj || 'İşlem başarısız');
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'İşlem başarısız'));
    } finally {
      setActionLoading(null);
    }
  };

  const openSiparisFromTaslak = (taslakTip) => {
    setCreateTaslakTip(taslakTip);
    setCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setCreateModalOpen(false);
    setCreateTaslakTip(null);
    setTaslakRefreshKey((k) => k + 1);
  };

  return (
    <main className="main-content tech-service-siparis">
      <div className="content-header">
        <div>
          <button
            type="button"
            className="secondary-btn"
            onClick={goBack}
            style={{ marginBottom: '16px' }}
          >
            <i className="fas fa-arrow-left"></i>
            <span>Geri Dön</span>
          </button>
          <h1>Sipariş Sistemi</h1>
        </div>
        <button
          type="button"
          className="btn-add-new"
          onClick={() => openSiparisFromTaslak(null)}
        >
          <i className="fas fa-plus"></i>
          Yeni Sipariş (tür seç)
        </button>
      </div>

      <SiparisTaslaklariPanel
        refreshKey={taslakRefreshKey}
        onDevam={(tip) => openSiparisFromTaslak(tip)}
        onCleared={() => setTaslakRefreshKey((k) => k + 1)}
      />

      <div className="tabs">
        {TABS.map((tab) => {
          const n = sekmeSayisiForTpTab(tab.id, sekmeSayilari);
          return (
            <button
              key={tab.id}
              type="button"
              className={`tab-btn siparis-tab-with-badge ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              <span>{tab.label}</span>
              {typeof n === 'number' ? <span className="siparis-tab-badge">{n}</span> : null}
            </button>
          );
        })}
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <div className="loading-text">Siparişler yükleniyor</div>
          </div>
        ) : error ? (
          <div className="error-state">{error}</div>
        ) : siparisler.length === 0 ? (
          <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
            <i className="fas fa-shopping-cart" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
            <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Sipariş bulunamadı</div>
          </div>
        ) : (
          <>
          <div className="table-container">
            <table className="admins-table">
              <thead>
                <tr>
                  <th>Sipariş No</th>
                  <th>Tarih</th>
                  <th>Tip</th>
                  <th>Servis Kaydı</th>
                  {activeTab === 'kargoda' && (
                    <>
                      <th>Kargo Firması</th>
                      <th>Takip No</th>
                    </>
                  )}
                  <th className="islemler-sutunu">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {siparisler.map((s, idx) => (
                  <tr key={getSiparisId(s) || idx}>
                    <td style={{ fontWeight: 500 }}>{s.siparisNumarasi}</td>
                    <td>{formatApiDate(s.olusturmaTarihi)}</td>
                    <td>{s.konsinye ? 'Konsinye' : s.garantiDahilinde ? 'Garanti dahilinde' : 'Doğrudan Sipariş'}</td>
                    <td>
                      {s.servisKaydiId ? (
                        <Link
                          to={`/service-records/${s.servisKaydiId}/update`}
                          style={{ color: 'var(--primary)', fontWeight: 500, textDecoration: 'none' }}
                        >
                          {s.servisKaydiNumarasi || s.servisKaydiId}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                    {activeTab === 'kargoda' && (
                      <>
                        <td>{s.kargoFirmasi ?? '—'}</td>
                        <td>{s.kargoTakipNo ?? '—'}</td>
                      </>
                    )}
                    <td className="table-actions islemler-sutunu">
                      <button
                        type="button"
                        className="category-action-btn edit-btn"
                        onClick={() => handleViewDetail(s)}
                        title="Detay"
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                      {activeTab === 'kargoda' && (
                        <button
                          type="button"
                          className="category-action-btn edit-btn"
                          onClick={() => handleKargoTeslimAl(s)}
                          title="Kargo teslim alındı"
                          disabled={actionLoading === getSiparisId(s)}
                          aria-label="Kargo teslim alındı"
                        >
                          <i className="fas fa-check-double"></i>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TableListFooter
            totalRecords={paginationMeta.total}
            currentPage={currentPage}
            totalPages={paginationMeta.totalPages || 1}
            onPageChange={setCurrentPage}
            loading={loading}
            embedded
          />
          </>
        )}
      </div>

      {detailModalOpen && selectedSiparis && (
        <SiparisDetayModal
          siparis={selectedSiparis}
          onClose={() => {
            setDetailModalOpen(false);
            setSelectedSiparis(null);
          }}
          onRefresh={loadSiparisler}
          isAdmin={false}
        />
      )}

      {createModalOpen && (
        <SiparisOlusturModal
          key={createTaslakTip || 'type-pick'}
          taslakTip={createTaslakTip || undefined}
          skipRestorePrompt={!!createTaslakTip}
          onClose={closeCreateModal}
          onSuccess={() => {
            loadSiparisler();
            refreshSekmeSayilari();
            setTaslakRefreshKey((k) => k + 1);
          }}
        />
      )}
    </main>
  );
};

export default TechServiceSiparisPage;
